<template>
  <div
    v-if="!redFlagLoading && !rolesLoading"
    class="page">
    <app-title
      v-if="!isEditing"
      :page="redFlag.title"
      :id="'title'"
      :header-size="'font-size-7 measure-narrow mb1'">
      <div
        slot="content"
        class="measure mb4">
        <vue-markdown :source="redFlag.description">{{ redFlag.description }}</vue-markdown>
        <button
          class="invisible-button"
          @click="isEditing =! isEditing">Edit</button>
      </div>
    </app-title>
    <app-edit-red-flag
      v-if="isEditing"
      :rf="redFlag"
      :page="page"
      :is-editing.sync="isEditing"
      class="grid row-4-height mb2 edit-flag"/>
    <section class="grid grid-columns-3_sm grid-gap-0">
      <section class="subtle p2 grid-column-span-2_sm">
        <header class="mb2 measure">
          <h2>How it affects your projects</h2>
          <p>Drag the sliders to set the impact this red flag has on each of your project roles. Focus on the comparison between roles more than the individual rating.</p>
          <p>The relative distribution will affect your estimate ranges depending on the composition of the estimate. For example, a red flag with an extreme impact on research will have a bigger effect on estimates that have a lot of research time.</p>
        </header>
        <lw-table
          v-if="redFlag.roles"
          :type="'lined'"
          :class="'c1 table--mobile'"
          :headings="[
            'Role',
            'Impact',
            ''
          ]"
          :alignment="[
            'left',
            'left',
            'right'
          ]"
          :widths="[
            '20',
            '60',
            '20'
          ]"
        >
          <tr
            v-for="(rfRole, key, index) in roles"
            :key="index">
            <td class="display pt1 bold font-size-4 role-name">{{ rfRole.name }}</td>
            <td class="range">
              <lw-range-input
                :id="'role-' + index"
                :name="'role-' + index"
                :min="0"
                :max="5"
                :step="1"
                :class="'pb0 mb0'"
                v-model.number="redFlag.roles[rfRole.id].impact"
                @input="editRedFlag"
              />
            </td>
            <td class="pt1 impact align-right">
              <div><span class="extra-condensed bold font-size-7">{{ redFlag.roles[rfRole.id].impact }}</span>/5</div>
              <div class="wide medium uppercase font-size-1">{{ impactLevel(redFlag.roles[rfRole.id].impact) }}</div>
            </td>
          </tr>
        </lw-table>
        <div v-else>
          <h3 class="dark-red-text">You don&rsquo;t have any roles setup.</h3>
          <p><router-link :to="{ name: 'settings'}">Add your organization&rsquo;s roles</router-link> to set your red flag&rsquo;s impact.</p>
        </div>
      </section>
      <section class="p2 reverse">
        <h2>Impact distribution profile</h2>
        <p>How this red flag affects a project or estimate.</p>
        <lw-radar-chart
          v-if="redFlagImpact"
          :chart-data="redFlagImpact"
          :title="''"
        />
      </section>
      <div class="align-right grid-column-full mt2 mb-margin">
        <button
          class="button accent large-button full-button_mb"
          @click="add">+ Add another red flag</button>
      </div>
    </section>

  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import RadarChart from '../livewires/RadarChart'
import Table from '../livewires/Table'
import RangeInput from '../livewires/RangeInput'
import EditRedFlag from './EditRedFlag'
import VueMarkdown from 'vue-markdown'
import page from '../mixins/page'

export default {
  components: {
    lwTable: Table,
    lwRangeInput: RangeInput,
    appEditRedFlag: EditRedFlag,
    lwRadarChart: RadarChart,
    vueMarkdown: VueMarkdown
  },
  mixins: [ page ],
  data () {
    return {
      page: this.$route.params.id,
      rolesList: [],
      isEditing: false,
      isConfirming: false
    }
  },
  computed: {
    ...mapGetters([
      'roles',
      'redFlagLoading',
      'rolesLoading'
    ]),
    redFlag () {
      return this.$store.getters.redFlag(this.page)
    },
    redFlagImpact () {
      return this.$store.getters.redFlagImpact(this.page)
    }
  },
  methods: {
    role (key) {
      return this.$store.getters.role(key)
    },
    editRedFlag () {
      this.$store.dispatch('saveState', false)
      let updatedFlag = { ...this.redFlag }
      updatedFlag.roles = this.redFlag.roles
      updatedFlag.title = this.redFlag.title
      updatedFlag.description = this.redFlag.description
      updatedFlag.id = this.page
      this.$store.dispatch('editRedFlag', updatedFlag).then(response => {
        this.$emit('redrawChart')
      })
    },
    impactLevel (level) {
      const impacts = [
        'No',
        'Very low',
        'Low',
        'Moderate',
        'High',
        'Extreme'
      ]
      return impacts[level] + ' impact'
    },
    add () {
      this.$router.push({ name: 'newRedFlag' })
    }
  }
}
</script>

<style scoped>
  .edit-flag {
    animation: slide-in-top 0.2s ease;
  }
  @media only screen and (max-width: 48em) {
    .role-name {
      margin-top: calc(var(--line) * 0.625);
      margin-bottom: 0;
    }
    .range {
      padding-left: 0;
      padding-right: 0;
      grid-column: span 2;
    }
    .impact {
      display: flex !important;
      grid-column: span 2;
      align-items: flex-end;
      justify-content: space-between;
      margin-bottom: calc(var(--line) * 0.125);
    }
    .impact div {
      width: 50%;
    }
    .impact div:first-child {
      text-align: right;
      order: 1;
    }
  }
  @media only screen and (min-width: 48em) {
    .range {
      padding-top: calc(var(--line) * 0.625);
      padding-bottom: calc(var(--line) * 0.5);
    }
    .impact {
      padding-bottom: 0;
    }
  }
</style>
